import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Avatar, Button, Form, Spin } from "antd";
import InfiniteScroll from "react-infinite-scroller";
import { useTranslation } from "react-i18next";
import { EyeOutlined } from "@ant-design/icons";
import { css } from "@emotion/css";
import classname from "classname";
import _ from "lodash";

import { useDebounceValue } from "hooks";
import {
  cleanBlankValue,
  generateCardName,
  parsePaginateData,
} from "utils/helper";
import { randomColors } from "utils/static";
import SearchWidget from "components/UI/SearchWidget";
import ListSuggestionTable from "components/UI/ListSuggestionForm/ListSuggestionTable";
import DetailSelectedModal from "components/UI/ListSuggestionForm/DetailSelectedModal";
import Skeleton from "components/UI/Skeleton";

const ListSuggestionForm = ({
  id,
  name,
  heightScroll = 500,
  formItemProps = {},
  payload = {},
  query = () => {},
  optionsQuery = {},
  formatOptions,
  withPropertyPayload,
  disabled = false,
  title,
  titleColumn,
  withSelectAll,
}) => {
  const { t } = useTranslation();
  const scrollRef = useRef();
  const form = Form.useFormInstance();
  const watchValue = Form.useWatch(name, form);

  const [params, setParams] = useState({
    search: "",
    per_page: 25,
  });
  const [isSelectedAll, setIsSelectedAll] = useState(false);
  const [visibleDetail, setVisibleDetail] = useState(false);
  const debouncedSearch = useDebounceValue(params?.search, 1000);

  const payloadQuery = useMemo(
    () =>
      cleanBlankValue({
        per_page: params.per_page,
        search: debouncedSearch,
        ...payload,
      }),
    [params, debouncedSearch, payload]
  );

  const formattedPayload = useMemo(() => {
    if (withPropertyPayload) {
      return {
        payload: payloadQuery,
      };
    }
    return payloadQuery;
  }, [withPropertyPayload, payloadQuery]);

  const { status, data, fetchNextPage, isFetchingNextPage, hasNextPage } =
    query(formattedPayload, { ...optionsQuery });

  const formattedData = useMemo(
    () => {
      const formatted = parsePaginateData(data, true);
      if (typeof formatOptions === "function") {
        formatted.data = formatOptions(formatted?.data);
      }
      return formatted;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, data?.pages, formatOptions]
  );

  const filterHandler = useCallback(
    (values) => setParams((prev) => ({ ...prev, ...values })),
    []
  );

  const selectRowHandler = useCallback(
    (record, selected) => {
      const currentValue = watchValue || [];

      if (selected) {
        const isAlreadySelected = currentValue.some(
          (item) => item?.value === record?.value
        );
        if (!isAlreadySelected) {
          form.setFieldValue(name, [...currentValue, record]);
        }
      } else {
        const updatedValue = currentValue.filter(
          (item) => item?.value !== record?.value
        );
        form.setFieldValue(name, updatedValue);
      }

      form.validateFields([name]);
    },
    [form, name, watchValue]
  );

  const handleFetchNextPage = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  const showDetailHandler = useCallback(() => {
    setVisibleDetail(true);
  }, []);

  const hideDetailHandler = useCallback(() => {
    setVisibleDetail(false);
  }, []);

  const selectRowAllHandler = useCallback(
    (selected, selectedRow) => {
      if (selected) {
        setIsSelectedAll(true);
        filterHandler({ per_page: formattedData?.total || 25 });
      } else {
        setIsSelectedAll(false);
        form.setFieldValue(name, []);
      }
    },
    [filterHandler, formattedData, name, form]
  );

  const selectAllHandler = useCallback(() => {
    form.setFieldValue(name, formattedData?.data);
    setIsSelectedAll(false);
  }, [form, name, formattedData]);

  useEffect(() => {
    if (
      status !== "loading" &&
      isSelectedAll &&
      formattedData?.data?.length === formattedData?.total
    ) {
      selectAllHandler();
    }
  }, [selectAllHandler, isSelectedAll, formattedData, status]);

  return (
    <>
      <Form.Item
        id={id}
        name={name}
        className={classname(
          `${css`
            .ant-form-item-control-input {
              min-height: 0 !important;
            }
          `}`,
          {
            "mb-0": _.isEmpty(watchValue),
          }
        )}
        {...formItemProps}
      >
        <div
          className={classname("flex flex-row items-center gap-2", {
            hidden: _.isEmpty(watchValue),
          })}
        >
          <Avatar.Group
            max={{
              count: 5,
              popover: {
                trigger: [],
              },
            }}
          >
            {(watchValue || [])?.map((row, index) =>
              row && row?.hr_avatar_url ? (
                <Avatar
                  key={index}
                  src={row?.hr_avatar_url}
                  className="border-none bg-white w-[30px] h-[30px]"
                />
              ) : (
                <Avatar
                  key={index}
                  style={{ backgroundColor: randomColors[index % 10] }}
                  className={`border-none w-[30px] h-[30px]`}
                >
                  {generateCardName(row?.label || "")}
                </Avatar>
              )
            )}
          </Avatar.Group>
          <Button
            icon={<EyeOutlined />}
            type="text"
            className="text-blue1"
            onClick={showDetailHandler}
          >
            {t("tooltip.see_more")}
          </Button>
        </div>
      </Form.Item>
      <div
        className={classname("flex flex-row justify-end", {
          hidden:
            (_.isEmpty(formattedData?.data) && !params?.search) || !!disabled,
        })}
      >
        <SearchWidget
          value={params.search}
          onChange={(e) => filterHandler({ search: e?.target?.value })}
        />
      </div>
      {!disabled && (
        <Skeleton loading={status === "loading"}>
          <div
            className={`max-h-[${heightScroll}px] overflow-auto my-5`}
            ref={scrollRef}
          >
            <InfiniteScroll
              initialLoad={false}
              pageStart={1}
              loadMore={handleFetchNextPage}
              hasMore={!!hasNextPage}
              useWindow={false}
              getScrollParent={() => scrollRef.current}
            >
              <ListSuggestionTable
                loading={status === "loading"}
                params={params}
                data={formattedData}
                titleColumn={titleColumn}
                selectedRowKeys={(watchValue || [])?.map((item) => item?.id)}
                withSelectAll={withSelectAll}
                onSelectRow={selectRowHandler}
                onSelectRowAll={selectRowAllHandler}
              />
              {!!isFetchingNextPage && (
                <div className="flex items-center justify-center my-3">
                  <Spin />
                </div>
              )}
            </InfiniteScroll>
          </div>
        </Skeleton>
      )}
      <DetailSelectedModal
        visible={visibleDetail}
        name={name}
        data={watchValue}
        title={title}
        titleColumn={titleColumn}
        disabled={disabled}
        onCancel={hideDetailHandler}
      />
    </>
  );
};

export default ListSuggestionForm;
